import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { trackAddToWishlist } from "@/lib/analytics";

type WishlistItem = {
  id?: string;
  user_id?: string;
  product_id: string;
  products?: {
    name?: string;
    price?: number;
  } | null;
};

type ToggleWishlistInput =
  | string
  | {
      productId: string;
      productName?: string;
      productPrice?: number;
    };

const normalizeInput = (input: ToggleWishlistInput) =>
  typeof input === "string" ? { productId: input } : input;

export const useWishlist = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const wishlistQueryKey = ["wishlist", user?.id];

  const { data: wishlistItems = [], isLoading } = useQuery<WishlistItem[]>({
    queryKey: wishlistQueryKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("wishlists")
        .select("*, products(*)")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as WishlistItem[];
    },
    enabled: !!user,
  });

  const wishlistProductIds = new Set(wishlistItems.map((w) => w.product_id));

  const toggleWishlist = useMutation({
    mutationFn: async (input: ToggleWishlistInput) => {
      if (!user) throw new Error("יש להתחבר כדי להוסיף למועדפים");

      const { productId } = normalizeInput(input);
      const cachedItems = queryClient.getQueryData<WishlistItem[]>(wishlistQueryKey) ?? [];
      const isInWishlist = cachedItems.some((item) => item.product_id === productId);

      if (isInWishlist) {
        const { error } = await supabase
          .from("wishlists")
          .delete()
          .eq("user_id", user.id)
          .eq("product_id", productId);
        if (error) throw error;
        return { added: false, productId };
      }

      const { error } = await supabase
        .from("wishlists")
        .insert({ user_id: user.id, product_id: productId });
      if (error) throw error;
      return { added: true, productId };
    },
    onMutate: async (input: ToggleWishlistInput) => {
      if (!user) return { previousItems: [] as WishlistItem[] };

      const { productId, productName, productPrice } = normalizeInput(input);
      await queryClient.cancelQueries({ queryKey: wishlistQueryKey });

      const previousItems = queryClient.getQueryData<WishlistItem[]>(wishlistQueryKey) ?? [];
      const isInWishlist = previousItems.some((item) => item.product_id === productId);

      const nextItems = isInWishlist
        ? previousItems.filter((item) => item.product_id !== productId)
        : [
            {
              product_id: productId,
              user_id: user.id,
              products:
                productName && typeof productPrice === "number"
                  ? { name: productName, price: productPrice }
                  : null,
            },
            ...previousItems,
          ];

      queryClient.setQueryData(wishlistQueryKey, nextItems);
      return { previousItems };
    },
    onSuccess: (result, input) => {
      const { productId, productName, productPrice } = normalizeInput(input);
      queryClient.invalidateQueries({ queryKey: wishlistQueryKey });
      toast.success(result.added ? "נוסף למועדפים ❤️" : "הוסר מהמועדפים");

      if (result.added && productName && typeof productPrice === "number") {
        trackAddToWishlist(productId, productName, productPrice);
      }
    },
    onError: (err: Error, _input, context) => {
      if (context?.previousItems) {
        queryClient.setQueryData(wishlistQueryKey, context.previousItems);
      }
      toast.error(err.message);
    },
  });

  return {
    wishlistItems,
    wishlistProductIds,
    isLoading,
    toggleWishlist: toggleWishlist.mutate,
    isInWishlist: (productId: string) => wishlistProductIds.has(productId),
    count: wishlistItems.length,
  };
};
